import React, { useState, useMemo, useEffect } from 'react';
import { colors } from '../../utils/colors';
import type { Teacher } from '../../utils/types';
import type { SubjectAssignment, ExamInfo, ExamStudent, ExamSubjectMeta } from './types';
import { MOCK_ASSIGNMENTS, MOCK_EXAMS, buildRoster } from './mockData';
import MarksEntrySheet from './MarksEntrySheet';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  teacher: Teacher;
  onBack: () => void;
}

type View = 'assignments' | 'exams' | 'students';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function fmtShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

function fmtRange(startISO: string, endISO: string): string {
  if (startISO === endISO) return fmtShortDate(startISO);
  return `${fmtShortDate(startISO)} – ${fmtShortDate(endISO)}`;
}

const CATEGORY_LABEL: Record<ExamInfo['category'], string> = {
  unit_test: 'Unit Test',
  mid_term: 'Mid Term',
  final: 'Final',
  other: 'Other',
};

// ─── Page shell ───────────────────────────────────────────────────────────────

const PageShell: React.FC<{
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}> = ({ title, onBack, children }) => (
  <div style={{
    height: '100dvh', background: '#FDFCFE',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    fontFamily: "'Inter', sans-serif", position: 'relative',
  }}>
    <div style={{
      padding: '30px 20px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      borderBottom: `1px solid ${colors.borderLight}`,
      background: '#fff', flexShrink: 0, zIndex: 10,
    }}>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 16, fontWeight: 500, color: colors.text,
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        {title}
      </button>
    </div>
    {children}
  </div>
);

// ─── Sub-components ───────────────────────────────────────────────────────────

const AssignmentCard: React.FC<{
  assignment: SubjectAssignment;
  index: number;
  onSelect: (a: SubjectAssignment) => void;
}> = ({ assignment, index, onSelect }) => (
  <button
    onClick={() => onSelect(assignment)}
    style={{
      width: '100%', padding: '16px 18px',
      background: colors.primaryBg,
      border: `1.5px solid ${colors.borderLight}`,
      borderRadius: 14, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 14,
      textAlign: 'left',
      animation: `slideUp 0.25s ease ${index * 0.04}s both`,
    }}
  >
    <div style={{
      width: 44, height: 44, borderRadius: 12, background: '#EEE3F7',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      </svg>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: colors.primary, marginBottom: 3 }}>
        {assignment.className} — Sec {assignment.sectionName}
      </div>
      <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>
        {assignment.subjectName} · {assignment.studentCount} students
      </div>
    </div>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </button>
);

const StudentMarksRow: React.FC<{
  student: ExamStudent;
  subject: ExamSubjectMeta;
  index: number;
  onEdit: (student: ExamStudent) => void;
}> = ({ student, subject, index, onEdit }) => {
  const val = student.marks[subject.subjectId] ?? null;
  const isFilled = val !== null && val !== undefined;
  const pct = isFilled ? Math.round((val / subject.maxMarks) * 100) : null;
  const passed = isFilled && val >= subject.minMarks;
  const failed = isFilled && val < subject.minMarks;

  const rollBg = !isFilled ? colors.primary : passed ? '#22c55e' : '#e04444';
  const rowBg = !isFilled ? '#fff' : passed ? '#f0fdf4' : '#fee2e2';
  const rowBorder = !isFilled ? colors.borderLight : passed ? '#86efac' : '#fecaca';

  return (
    <button
      onClick={() => onEdit(student)}
      style={{
        width: '100%', background: rowBg,
        border: `1px solid ${rowBorder}`, borderRadius: 14,
        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
        animation: `slideUp 0.2s ease ${Math.min(index * 0.02, 0.2)}s both`,
        transition: 'background 0.2s, border-color 0.2s',
        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: 40, height: 40, borderRadius: '50%',
          background: rollBg, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: 13, fontWeight: 800, color: '#fff',
        }}
      >
        {student.rollNo}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14, fontWeight: 700, color: colors.text,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {student.name}
        </div>
        <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
          {student.uid}
        </div>
      </div>
      {isFilled ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: passed ? '#166534' : '#991b1b', lineHeight: 1 }}>
              {val}
              <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600 }}>
                /{subject.maxMarks}
              </span>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: passed ? '#166534' : '#991b1b', marginTop: 3 }}>
              {pct}% · {passed ? 'PASS' : 'FAIL'}
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke={colors.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </div>
      ) : (
        <span
          style={{
            fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 20,
            background: '#fffbeb', color: '#d97706', border: '1px solid #fcd34d',
            whiteSpace: 'nowrap',
          }}
        >
          Add marks
        </span>
      )}
    </button>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ExamMarks: React.FC<Props> = ({ onBack }) => {
  const [view, setView] = useState<View>('assignments');
  const [assignments] = useState<SubjectAssignment[]>(MOCK_ASSIGNMENTS);
  const [selectedAssignment, setSelectedAssignment] = useState<SubjectAssignment | null>(null);
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
  const [students, setStudents] = useState<ExamStudent[]>([]);
  const [editingStudent, setEditingStudent] = useState<ExamStudent | null>(null);
  const [search, setSearch] = useState('');

  // ── Filter ongoing exams for this assignment ────────────────────────────
  const ongoingExams = useMemo(() => {
    if (!selectedAssignment) return [];
    return MOCK_EXAMS.filter(
      (e) =>
        e.classId === selectedAssignment.classId &&
        e.sectionId === selectedAssignment.sectionId &&
        e.status === 'ongoing' &&
        e.subjects.some((s) => s.subjectId === selectedAssignment.subjectId),
    );
  }, [selectedAssignment]);

  const selectedExam = useMemo(
    () => ongoingExams.find((e) => e.id === selectedExamId) ?? null,
    [ongoingExams, selectedExamId],
  );

  const subjectMeta: ExamSubjectMeta | null = useMemo(() => {
    if (!selectedExam || !selectedAssignment) return null;
    return (
      selectedExam.subjects.find((s) => s.subjectId === selectedAssignment.subjectId) ?? null
    );
  }, [selectedExam, selectedAssignment]);

  // ── Load roster when assignment changes ─────────────────────────────────
  useEffect(() => {
    if (!selectedAssignment) return;
    setStudents(
      buildRoster(
        selectedAssignment.classId,
        selectedAssignment.sectionId,
        selectedAssignment.studentCount,
      ),
    );
  }, [selectedAssignment]);

  // ── Auto-pick first ongoing exam ────────────────────────────────────────
  useEffect(() => {
    if (ongoingExams.length > 0 && selectedExamId === null) {
      setSelectedExamId(ongoingExams[0].id);
    }
  }, [ongoingExams, selectedExamId]);

  // ── Lock page scroll while sheet is open ────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = editingStudent ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [editingStudent]);

  const filteredStudents = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.toLowerCase();
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.rollNo.includes(q) ||
        s.uid.toLowerCase().includes(q),
    );
  }, [students, search]);

  const stats = useMemo(() => {
    if (!subjectMeta) return { total: students.length, filled: 0, pending: students.length };
    const filled = students.filter(
      (s) => s.marks[subjectMeta.subjectId] !== null && s.marks[subjectMeta.subjectId] !== undefined,
    ).length;
    return { total: students.length, filled, pending: students.length - filled };
  }, [students, subjectMeta]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleAssignmentSelect = (a: SubjectAssignment) => {
    setSelectedAssignment(a);
    setSelectedExamId(null);
    setSearch('');
    setView('exams');
  };

  const handleOpenStudents = () => setView('students');

  const handleSaveMarks = (studentId: number, subjectId: number, marks: number | null) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.id === studentId ? { ...s, marks: { ...s.marks, [subjectId]: marks } } : s,
      ),
    );
  };

  const goBackFromExams = () => {
    setSelectedAssignment(null);
    setSelectedExamId(null);
    setView('assignments');
  };

  const goBackFromStudents = () => {
    setSearch('');
    setView('exams');
  };

  const globalStyles = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes sheetSlideUp {
      from { transform: translateY(100%); }
      to   { transform: translateY(0);   }
    }
  `;

  // ─── VIEW 1: Assignments list ─────────────────────────────────────────
  if (view === 'assignments') {
    return (
      <PageShell title="Exam marks" onBack={onBack}>
        <style>{globalStyles}</style>

        <div style={{ padding: '16px 20px 8px', flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.5px' }}>
            YOUR ASSIGNED CLASSES
          </div>
          <div style={{ fontSize: 13, color: colors.textLight, marginTop: 4 }}>
            Pick a class + subject to enter exam marks
          </div>
        </div>

        <div
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '10px 20px 30px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          {assignments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textMuted, fontSize: 14 }}>
              No classes assigned yet.
            </div>
          ) : (
            assignments.map((a, i) => (
              <AssignmentCard
                key={a.assignmentId}
                assignment={a}
                index={i}
                onSelect={handleAssignmentSelect}
              />
            ))
          )}
        </div>
      </PageShell>
    );
  }

  // ─── VIEW 2: Ongoing exams ─────────────────────────────────────────────
  if (view === 'exams' && selectedAssignment) {
    return (
      <PageShell title="Exam marks" onBack={goBackFromExams}>
        <style>{globalStyles}</style>

        {/* Context banner */}
        <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
          <div
            style={{
              background: colors.primaryDark,
              borderRadius: 12, padding: '12px 16px',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: '#EEE3F7', letterSpacing: '0.5px' }}>
              CLASS · SUBJECT
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
              {selectedAssignment.className} — Sec {selectedAssignment.sectionName}
              <span style={{ color: '#EEE3F7', fontWeight: 500 }}> · {selectedAssignment.subjectName}</span>
            </div>
          </div>
        </div>

        {/* Ongoing tabs label */}
        <div
          style={{
            padding: '18px 20px 6px', display: 'flex', alignItems: 'center', gap: 8,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: '#22c55e', boxShadow: '0 0 0 3px #dcfce7',
            }}
          />
          <span style={{ fontSize: 12, fontWeight: 800, color: colors.text, letterSpacing: '0.4px' }}>
            ONGOING EXAMS
          </span>
          <span
            style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: '#dcfce7', color: '#166534',
            }}
          >
            {ongoingExams.length}
          </span>
        </div>

        {/* Ongoing exams list */}
        <div
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '4px 20px 30px', display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          {ongoingExams.length === 0 ? (
            <div
              style={{
                textAlign: 'center', padding: '48px 20px',
                background: '#fff', borderRadius: 14,
                border: `1px dashed ${colors.borderLight}`,
                color: colors.textMuted, fontSize: 13,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>—</div>
              No ongoing exams for this subject right now.
            </div>
          ) : (
            ongoingExams.map((exam, i) => {
              const sub = exam.subjects.find((s) => s.subjectId === selectedAssignment.subjectId);
              return (
                <button
                  key={exam.id}
                  onClick={() => {
                    setSelectedExamId(exam.id);
                    handleOpenStudents();
                  }}
                  style={{
                    width: '100%', padding: '16px 18px',
                    background: '#fff',
                    border: `1.5px solid ${colors.borderLight}`,
                    borderRadius: 14, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 14,
                    textAlign: 'left', fontFamily: 'inherit',
                    animation: `slideUp 0.25s ease ${i * 0.04}s both`,
                  }}
                >
                  <div
                    style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: '#dcfce7',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                      stroke="#166534" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="9" y1="15" x2="15" y2="15" />
                      <line x1="9" y1="11" x2="15" y2="11" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: colors.text }}>
                      {exam.name}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3, fontWeight: 600 }}>
                      {CATEGORY_LABEL[exam.category]} · {fmtRange(exam.startDate, exam.endDate)}
                    </div>
                    {sub && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                        <span
                          style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: colors.primaryBg, color: colors.primary,
                            border: `1px solid ${colors.borderLight}`,
                          }}
                        >
                          Max {sub.maxMarks}
                        </span>
                        <span
                          style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
                          }}
                        >
                          Min {sub.minMarks}
                        </span>
                      </div>
                    )}
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              );
            })
          )}
        </div>
      </PageShell>
    );
  }

  // ─── VIEW 3: Students list + marks entry ───────────────────────────────
  if (view === 'students' && selectedAssignment && selectedExam && subjectMeta) {
    return (
      <PageShell title={selectedExam.name} onBack={goBackFromStudents}>
        <style>{globalStyles}</style>

        {/* Context header */}
        <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
          <div
            style={{
              background: colors.primaryDark, borderRadius: 12,
              padding: '12px 16px',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: '#EEE3F7', letterSpacing: '0.5px' }}>
              {selectedAssignment.className.toUpperCase()} — SEC {selectedAssignment.sectionName} · {subjectMeta.subjectName.toUpperCase()}
            </div>
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
                {selectedExam.name}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                    background: 'rgba(255,255,255,0.18)', color: '#fff',
                  }}
                >
                  Max {subjectMeta.maxMarks}
                </span>
                <span
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                    background: 'rgba(255,255,255,0.18)', color: '#fff',
                  }}
                >
                  Min {subjectMeta.minMarks}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ padding: '12px 20px 0', display: 'flex', gap: 8, flexShrink: 0 }}>
          <div
            style={{
              flex: 1, background: colors.primaryBg,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 12, padding: '10px 8px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: colors.primary }}>
              {stats.total}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.5px', marginTop: 2 }}>
              TOTAL
            </div>
          </div>
          <div
            style={{
              flex: 1, background: '#f0fdf4',
              border: '1px solid #86efac', borderRadius: 12,
              padding: '10px 8px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: '#166534' }}>
              {String(stats.filled).padStart(2, '0')}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#166534', letterSpacing: '0.5px', marginTop: 2 }}>
              ENTERED
            </div>
          </div>
          <div
            style={{
              flex: 1, background: '#fffbeb',
              border: '1px solid #fcd34d', borderRadius: 12,
              padding: '10px 8px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: '#92400e' }}>
              {String(stats.pending).padStart(2, '0')}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#92400e', letterSpacing: '0.5px', marginTop: 2 }}>
              PENDING
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#F3F4F6', borderRadius: 12,
              padding: '10px 14px',
              border: `1.5px solid ${colors.borderLight}`,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={colors.textMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search student by name or roll…"
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                fontSize: 14, color: colors.text, fontFamily: 'inherit',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 0, color: colors.textMuted, fontSize: 16, lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Students list */}
        <div
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '12px 20px 30px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          {filteredStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textMuted, fontSize: 14 }}>
              {search ? 'No students match your search.' : 'No students in this class.'}
            </div>
          ) : (
            filteredStudents.map((s, i) => (
              <StudentMarksRow
                key={s.id}
                student={s}
                subject={subjectMeta}
                index={i}
                onEdit={setEditingStudent}
              />
            ))
          )}
        </div>

        {editingStudent && (
          <MarksEntrySheet
            student={editingStudent}
            subject={subjectMeta}
            examName={selectedExam.name}
            onClose={() => setEditingStudent(null)}
            onSave={handleSaveMarks}
          />
        )}
      </PageShell>
    );
  }

  // Fallback (shouldn't hit)
  return (
    <PageShell title="Exam marks" onBack={onBack}>
      <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
        Something went wrong. Please go back.
      </div>
    </PageShell>
  );
};

export default ExamMarks;
